import React from 'react';

import { Select, variableVariantToFieldVariant } from '@aline/common';
import type { DropAnimation } from '@dnd-kit/core';
import { DragOverlay } from '@dnd-kit/core';
import { alpha, Typography, useTheme } from '@mui/material';
import type { Core } from '@pdftron/webviewer';
import type { DragField } from 'src/components/drag-and-drop/helpers';
import { FieldIcon } from 'src/components/FieldIcon';
import { CANNED_ICON_BY_VARIANT } from 'src/components/icons/CannedFieldIcons';
import { DynamicBlockIcon } from 'src/components/icons/DynamicBlockIcon';
import { IconColors } from 'src/components/icons/icon-utils';
import { SignatureFieldIcon } from 'src/components/icons/SignatureFieldIcon';
import {
  convertDimensionUsingZoom,
  defaultPDFFieldAttributes,
  pdfCheckboxWidgetBorderColor,
  ZoomConvertDir,
} from 'src/components/viewer';
import type { EditorMousePageCoords } from 'src/editor/editor-tab-state-registry';
import { Apollo } from 'src/gql/generated';
import { domElIdCreators, useDocumentIdFromUrl } from 'src/helpers';
import { useGetCurrentDoc } from 'src/hooks';
import type { ReactSetStateTuple } from 'src/types';
import { getWorkflowWasSentForSig } from 'src/workflow';
import { makeStyles } from 'tss-react/mui';

import { snapCursorToPoint } from './modifiers';

const useDragOverlayStyles = makeStyles()(() => ({
  dragOverlay: {
    // Keeps the wrapper snapped to the rendered preview for cursor-anchor math.
    width: 'fit-content',
  },
}));

interface AlineDragOverlayProps {
  documentViewer: Core.DocumentViewer | null;
  dragFieldTuple: ReactSetStateTuple<DragField | null>;
  mousePageCoords: EditorMousePageCoords | null;
  dropAnimation?: DropAnimation | null;
}

/**
 * DragOverlay is supposed to remain mounted at all times.  So, use AlineDragOverlayBody if we want to render nothing.
 * See - https://docs.dndkit.com/api-documentation/draggable/drag-overlay#usage.
 */
export function AlineDragOverlay(props: AlineDragOverlayProps): JSX.Element {
  const theme = useTheme();
  const { classes } = useDragOverlayStyles();
  const { dropAnimation, ...rest } = props;

  return (
    <DragOverlay
      dropAnimation={dropAnimation}
      // Note: Don't use restrictToWindowEdges modifier from DNDKit b/c it's busted w/ our use of custom drag preview.
      modifiers={[snapCursorToPoint]}
      zIndex={theme.zIndex.tooltip}
    >
      <div className={classes.dragOverlay} id={domElIdCreators.fieldOverlayEl()}>
        <AlineDragOverlayBody {...rest} />
      </div>
    </DragOverlay>
  );
}

function AlineDragOverlayBody(props: AlineDragOverlayProps): JSX.Element | null {
  const {
    documentViewer,
    dragFieldTuple: [dragField],
    mousePageCoords,
  } = props;

  // No item being dragged.
  if (!dragField) return null;

  // Item being dragged outside page.
  if (!mousePageCoords) return <DragOutsidePage dragField={dragField} />;

  return <DragFieldOverlay documentViewer={documentViewer} dragField={dragField} />;
}

export const dragOutsidePageElSizePx = 40;
const useDragOutsidePageStyles = makeStyles()(theme => ({
  dragElement: {
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 3,
    boxSizing: 'border-box' as const,
    cursor: 'grabbing',
    display: 'flex',
    height: dragOutsidePageElSizePx,
    justifyContent: 'center',
    width: dragOutsidePageElSizePx,
  },
}));

interface DragOutsidePageProps {
  dragField: DragField;
}

function DragOutsidePage({ dragField }: DragOutsidePageProps): JSX.Element {
  const { classes } = useDragOutsidePageStyles();

  return (
    <div className={classes.dragElement} data-cy={Select.document.fields.dragOutsidePageOverlay}>
      <DragFieldIcon dragField={dragField} />
    </div>
  );
}

export function DragFieldIcon(props: DragOutsidePageProps): JSX.Element {
  const { dragField } = props;

  if (dragField.__typename === 'SignatureField') return <SignatureFieldIcon />;
  if (dragField.__typename === 'CannedField') {
    const Icon = CANNED_ICON_BY_VARIANT[dragField.variant];
    return <Icon />;
  }
  if (dragField.isDynamic) return <DynamicBlockIcon />;

  return <FieldIcon fieldVariant={variableVariantToFieldVariant(dragField.variant)} />;
}

const useDragFieldStyles = makeStyles<{ zoom: number }>()((theme, { zoom }) => ({
  dropCursor: {
    cursor: 'text',
    // Needs to be same size as other dragging el otherwise DND flickers and goes nuts...
    height: dragOutsidePageElSizePx,
    width: dragOutsidePageElSizePx,
  },
  pdfCheckboxField: {
    backgroundColor: 'transparent',
    border: `1px solid ${pdfCheckboxWidgetBorderColor}`,
    boxSizing: 'border-box' as const,
    cursor: 'grabbing',
  },
  pdfNonSigField: {
    backgroundColor: alpha(IconColors.Primary, 0.1),
    border: `1px solid ${IconColors.Primary}`,
    borderRadius: 3,
    boxSizing: 'border-box' as const,
    cursor: 'grabbing',
    padding: defaultPDFFieldAttributes.paddingLeftPx,
  },
  pdfSigField: {
    alignItems: 'center',
    backgroundColor: theme.appColors.defaultFieldBackground,
    border: `1px dashed ${theme.palette.primary.main}`,
    borderRadius: 3,
    boxSizing: 'border-box' as const,
    cursor: 'grabbing',
    display: 'flex',
    padding: theme.spacing(1),
  },
  signText: {
    fontSize: convertDimensionUsingZoom(defaultPDFFieldAttributes.fontSizePx, {
      direction: ZoomConvertDir.To,
      zoom,
    }),
    marginLeft: theme.spacing(1),
  },
}));

interface DragFieldOverlayProps {
  documentViewer: Core.DocumentViewer | null;
  dragField: DragField;
}

function DragFieldOverlay({
  documentViewer,
  dragField,
}: DragFieldOverlayProps): JSX.Element | null {
  // AppLayout renders above `/doc/:documentId/*`, so useParams() returns {}.
  // Read the id from the URL so the overlay can look up the current document.
  const documentId = useDocumentIdFromUrl();
  const { document } = useGetCurrentDoc(documentId);
  const zoom = documentViewer?.getZoomLevel() ?? 1;
  const { classes } = useDragFieldStyles({ zoom });

  if (!document) return null;

  switch (document.latestVersion.__typename) {
    case 'StaticFileVersion': {
      const isSignature = dragField.__typename === 'SignatureField';
      if (isSignature) {
        const wasSentForSignature = getWorkflowWasSentForSig(document.workflow);

        return (
          <div
            className={classes.pdfSigField}
            data-cy={Select.document.fields.dragInPageOverlay}
            style={{
              height: defaultPDFFieldAttributes.sigFieldHeightPx * zoom,
              width: defaultPDFFieldAttributes.sigFieldWidthPx * zoom,
            }}
          >
            <SignatureFieldIcon />
            <Typography noWrap className={classes.signText}>
              {wasSentForSignature ? 'Click to sign' : 'Click to edit signature'}
            </Typography>
          </div>
        );
      }

      if (dragField.variant === Apollo.MetadataVariant.Checkbox) {
        const widgetSizePx = defaultPDFFieldAttributes.fontSizePx * zoom;

        return (
          <div
            className={classes.pdfCheckboxField}
            data-cy={Select.document.fields.dragInPageOverlay}
            style={{ height: widgetSizePx, width: widgetSizePx }}
          />
        );
      }

      return (
        <div
          className={classes.pdfNonSigField}
          data-cy={Select.document.fields.dragInPageOverlay}
          style={{
            height: defaultPDFFieldAttributes.nonSigFieldHeightPx * zoom,
            width: defaultPDFFieldAttributes.nonSigFieldWidthPx * zoom,
          }}
        />
      );
    }
    case 'DocumentVersion':
      return (
        <div className={classes.dropCursor} data-cy={Select.document.fields.dragInPageOverlay} />
      );
  }
}
